import { useState, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/contexts/ToastContext';
import { nutritionApi } from '@/lib/api';
import { formatDateISO } from '@/lib/utils';
import type { Meal, MealType } from '@/types';
import { MEAL_TYPE_LABELS } from '@/types';
import { PlusIcon, TrashIcon, ChevronLeftIcon, ChevronRightIcon } from '@/components/Icons';
import LoadingSpinner from '@/components/LoadingSpinner';
import Modal from '@/components/Modal';

const GLASS_ML = 350;
const WATER_GOAL = 2000;

// Get effective date considering 2 AM reset
function getEffectiveDate(): string {
  const now = new Date();
  if (now.getHours() < 2) {
    now.setDate(now.getDate() - 1);
  }
  return formatDateISO(now);
}

function formatDisplayDate(dateStr: string): string {
  const date = new Date(dateStr + 'T12:00:00');
  const today = getEffectiveDate();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = formatDateISO(yesterday);
  
  if (dateStr === today) return 'Сегодня';
  if (dateStr === yesterdayStr) return 'Вчера';
  
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}

export default function NutritionPage() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const dateInputRef = useRef<HTMLInputElement>(null);
  const [selectedDate, setSelectedDate] = useState(getEffectiveDate());
  const [showMealModal, setShowMealModal] = useState(false);
  const [editingMeal, setEditingMeal] = useState<Meal | null>(null);
  
  // Meal form state
  const [mealType, setMealType] = useState<MealType>('breakfast');
  const [mealName, setMealName] = useState('');
  const [calories, setCalories] = useState('');
  const [protein, setProtein] = useState('');
  const [fat, setFat] = useState('');
  const [carbs, setCarbs] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Fetch water data
  const { data: waterData, isLoading: waterLoading } = useQuery({
    queryKey: ['water', selectedDate],
    queryFn: async () => {
      const res = await nutritionApi.getWater(selectedDate);
      return res.success ? res.data : { date: selectedDate, glasses: 0, total_ml: 0 };
    },
  });

  // Fetch meals data
  const { data: mealsData, isLoading: mealsLoading } = useQuery({
    queryKey: ['meals', selectedDate],
    queryFn: async () => {
      const res = await nutritionApi.getMeals(selectedDate);
      return res.success ? res.data : { date: selectedDate, meals: [], totals: { calories: 0, protein: 0, fat: 0, carbs: 0 } };
    },
  });

  const glasses = waterData?.glasses || 0;
  const totalMl = waterData?.total_ml || 0;

  const toggleGlass = async (index: number) => {
    if (!canEdit) return; // Don't allow editing past dates
    
    const bit = 1 << index;
    const newGlasses = glasses ^ bit; // Toggle bit
    
    await nutritionApi.updateWater(newGlasses, selectedDate);
    queryClient.invalidateQueries({ queryKey: ['water', selectedDate] });
  };

  const isGlassFilled = (index: number) => (glasses & (1 << index)) !== 0;

  const changeDate = (delta: number) => {
    const date = new Date(selectedDate + 'T12:00:00');
    date.setDate(date.getDate() + delta);
    const newDate = formatDateISO(date);
    // Don't allow future dates
    if (newDate <= getEffectiveDate()) {
      setSelectedDate(newDate);
    }
  };

  const openAddMealModal = () => {
    setEditingMeal(null);
    setMealType('breakfast');
    setMealName('');
    setCalories('');
    setProtein('');
    setFat('');
    setCarbs('');
    setNote('');
    setError('');
    setShowMealModal(true);
  };

  const openEditMealModal = (meal: Meal) => {
    setEditingMeal(meal);
    setMealType(meal.meal_type);
    setMealName(meal.name);
    setCalories(meal.calories?.toString() || '');
    setProtein(meal.protein?.toString() || '');
    setFat(meal.fat?.toString() || '');
    setCarbs(meal.carbs?.toString() || '');
    setNote(meal.note || '');
    setError('');
    setShowMealModal(true);
  };

  const handleSubmitMeal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mealName.trim()) {
      setError('Введите название');
      return;
    }

    setSaving(true);
    setError('');

    const data = {
      date: selectedDate,
      meal_type: mealType,
      name: mealName.trim(),
      calories: calories ? parseFloat(calories) : undefined,
      protein: protein ? parseFloat(protein) : undefined,
      fat: fat ? parseFloat(fat) : undefined,
      carbs: carbs ? parseFloat(carbs) : undefined,
      note: note.trim() || undefined,
    };

    const res = editingMeal
      ? await nutritionApi.updateMeal(editingMeal.id, data)
      : await nutritionApi.createMeal(data);

    if (res.success) {
      queryClient.invalidateQueries({ queryKey: ['meals', selectedDate] });
      setShowMealModal(false);
      showToast(editingMeal ? 'Запись обновлена' : 'Запись добавлена', 'success');
    } else {
      setError(res.error || 'Не удалось сохранить');
    }

    setSaving(false);
  };

  const handleDeleteMeal = async (meal: Meal) => {
    if (!confirm('Удалить этот приём пищи?')) return;
    
    const res = await nutritionApi.deleteMeal(meal.id);
    queryClient.invalidateQueries({ queryKey: ['meals', selectedDate] });
    setShowMealModal(false);
    if (res.success) {
      showToast('Запись удалена', 'success');
    }
  };

  const isToday = selectedDate === getEffectiveDate();
  const canEdit = isToday; // Only allow editing for today

  return (
    <div className="page-container">
      {/* Header with centered date navigation */}
      <header className="page-header flex items-center justify-center">
        <div className="flex items-center gap-1">
          <button
            onClick={() => changeDate(-1)}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ChevronLeftIcon className="w-5 h-5" />
          </button>
          
          {/* Hidden date input */}
          <input
            ref={dateInputRef}
            type="date"
            value={selectedDate}
            max={getEffectiveDate()}
            onChange={(e) => {
              if (e.target.value && e.target.value <= getEffectiveDate()) {
                setSelectedDate(e.target.value);
              }
            }}
            className="absolute opacity-0 w-0 h-0"
          />
          
          {/* Clickable date label */}
          <button
            onClick={() => dateInputRef.current?.showPicker()}
            className="px-4 py-2 text-base font-medium text-gray-900 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
          >
            <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
            </svg>
            {formatDisplayDate(selectedDate)}
          </button>
          
          <button
            onClick={() => changeDate(1)}
            disabled={isToday}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
          >
              <ChevronRightIcon className="w-5 h-5" />
            </button>
          </div>
      </header>

      <div className="p-4 space-y-6">
        {/* Water Section */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="section-title mb-0">Вода</h2>
            <span className="text-sm text-gray-500">
              {totalMl} / {WATER_GOAL} мл
            </span>
          </div>
          
          <div className="card p-4">
            {waterLoading ? (
              <div className="flex justify-center py-4">
                <LoadingSpinner />
              </div>
            ) : (
              <>
                {/* Progress bar */}
                <div className="h-2 bg-gray-100 rounded-full mb-4 overflow-hidden">
                  <div
                    className="h-full bg-primary-500 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min(100, (totalMl / WATER_GOAL) * 100)}%` }}
                  />
                </div>
                
                {/* Glasses */}
                <div className="flex justify-between gap-2">
                  {[0, 1, 2, 3, 4, 5].map((index) => {
                    const filled = isGlassFilled(index);
                    return (
                      <button
                        key={index}
                        onClick={() => toggleGlass(index)}
                        disabled={!canEdit}
                        className={`flex-1 flex flex-col items-center gap-1 p-2 rounded-lg transition-colors ${
                          canEdit ? 'hover:bg-gray-50' : 'cursor-not-allowed opacity-60'
                        }`}
                      >
                        <svg 
                          viewBox="0 0 40 52" 
                          className="w-10 h-12"
                          fill="none"
                        >
                          {/* Glass outline - wider at top, narrower at bottom */}
                          <path
                            d="M2 2 C2 0 4 0 6 0 L34 0 C36 0 38 0 38 2 L34 48 C34 50 32 52 28 52 L12 52 C8 52 6 50 6 48 L2 2 Z"
                            fill={filled ? '#e0f2fe' : '#f9fafb'}
                            stroke={filled ? '#0ea5e9' : '#e5e7eb'}
                            strokeWidth="2"
                            className="transition-all duration-300"
                          />
                          {/* Water fill with clip path for animation */}
                          <defs>
                            <clipPath id={`glass-clip-${index}`}>
                              <path d="M3 4 L6.5 47 C6.5 49 8 51 12 51 L28 51 C32 51 33.5 49 33.5 47 L37 4 Z" />
                            </clipPath>
                          </defs>
                          <rect
                            x="0"
                            y={filled ? 8 : 52}
                            width="40"
                            height="44"
                            fill="#38bdf8"
                            clipPath={`url(#glass-clip-${index})`}
                            className="transition-all duration-500 ease-out"
                          />
                        </svg>
                        <span className="text-xs text-gray-500">{GLASS_ML}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </section>

        {/* Meals Section */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="section-title mb-0">Приёмы пищи</h2>
            {canEdit && (
              <button 
                onClick={openAddMealModal} 
                className="w-9 h-9 rounded-full bg-primary-600 text-white flex items-center justify-center hover:bg-primary-700 transition-colors shadow-sm"
              >
                <PlusIcon className="w-5 h-5" />
              </button>
            )}
          </div>

          {mealsLoading ? (
            <div className="card p-8 flex justify-center">
              <LoadingSpinner />
            </div>
          ) : mealsData?.meals.length === 0 ? (
            <div className="card">
              <div className="empty-state">
                <p className="text-gray-500 mb-4">
                  {canEdit ? 'Приёмов пищи пока нет' : 'Нет записей за этот день'}
                </p>
                {canEdit && (
                  <button onClick={openAddMealModal} className="btn-primary">
                    Добавить первый
                  </button>
                )}
              </div>
            </div>
          ) : (
            <>
              {/* Totals card */}
              {mealsData && mealsData.totals.calories > 0 && (
                <div className="card p-4 mb-3">
                  <div className="grid grid-cols-4 gap-2 text-center">
                    <div>
                      <div className="text-lg font-bold text-gray-900">{mealsData.totals.calories}</div>
                      <div className="text-[10px] text-gray-500 uppercase">ккал</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-gray-900">{mealsData.totals.protein}</div>
                      <div className="text-[10px] text-gray-500 uppercase">белки</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-gray-900">{mealsData.totals.fat}</div>
                      <div className="text-[10px] text-gray-500 uppercase">жиры</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-gray-900">{mealsData.totals.carbs}</div>
                      <div className="text-[10px] text-gray-500 uppercase">углеводы</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Meals list */}
              <div className="card divide-y divide-gray-100">
                {mealsData?.meals.map((meal) => (
                  <div
                    key={meal.id}
                    onClick={() => canEdit && openEditMealModal(meal)}
                    className={`w-full p-4 text-left transition-colors ${
                      canEdit ? 'hover:bg-gray-50 cursor-pointer' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-400 uppercase">
                            {MEAL_TYPE_LABELS[meal.meal_type]}
                          </span>
                        </div>
                        <p className="font-medium text-gray-900 mt-0.5">{meal.name}</p>
                        {(meal.calories || meal.protein || meal.fat || meal.carbs) && (
                          <div className="flex gap-3 text-sm text-gray-500 mt-1">
                            {meal.calories && <span>{meal.calories} ккал</span>}
                            {meal.protein && <span>Б: {meal.protein}</span>}
                            {meal.fat && <span>Ж: {meal.fat}</span>}
                            {meal.carbs && <span>У: {meal.carbs}</span>}
                          </div>
                        )}
                      </div>
                      {canEdit && <ChevronRightIcon className="w-5 h-5 text-gray-300 mt-1" />}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>
      </div>

      {/* Spacer for bottom nav */}
      <div className="h-20" />

      {/* Add/Edit Meal Modal */}
      <Modal
        isOpen={showMealModal}
        onClose={() => setShowMealModal(false)}
        title={editingMeal ? 'Редактировать' : 'Новый приём пищи'}
      >
        <form onSubmit={handleSubmitMeal} className="space-y-4">
          {error && (
            <div className="p-3 text-sm text-red-700 bg-red-50 rounded-lg">
              {error}
            </div>
          )}

          {/* Meal type selector */}
          <div>
            <label className="label">Тип приёма пищи</label>
            <div className="grid grid-cols-4 gap-2">
              {(['breakfast', 'lunch', 'dinner', 'snack'] as MealType[]).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setMealType(type)}
                  className={`p-2 text-sm rounded-lg border transition-colors ${
                    mealType === type
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {MEAL_TYPE_LABELS[type]}
                </button>
              ))}
            </div>
          </div>

          {/* Name */}
          <div>
            <label htmlFor="mealName" className="label">
              Название *
            </label>
            <input
              id="mealName"
              type="text"
              value={mealName}
              onChange={(e) => setMealName(e.target.value)}
              className="input"
              placeholder="Что вы съели?"
              required
            />
          </div>

          {/* Macros - optional */}
          <div className="pt-3 border-t border-gray-100">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">
              Пищевая ценность (необязательно)
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="calories" className="label">Калории</label>
                <input
                  id="calories"
                  type="number"
                  inputMode="numeric"
                  value={calories}
                  onChange={(e) => setCalories(e.target.value)}
                  className="input"
                  placeholder="ккал"
                />
              </div>
              <div>
                <label htmlFor="protein" className="label">Белки</label>
                <input
                  id="protein"
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  value={protein}
                  onChange={(e) => setProtein(e.target.value)}
                  className="input"
                  placeholder="г"
                />
              </div>
              <div>
                <label htmlFor="fat" className="label">Жиры</label>
                <input
                  id="fat"
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  value={fat}
                  onChange={(e) => setFat(e.target.value)}
                  className="input"
                  placeholder="г"
                />
              </div>
              <div>
                <label htmlFor="carbs" className="label">Углеводы</label>
                <input
                  id="carbs"
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  value={carbs}
                  onChange={(e) => setCarbs(e.target.value)}
                  className="input"
                  placeholder="г"
                />
              </div>
            </div>
          </div>

          {/* Note */}
          <div>
            <label htmlFor="note" className="label">
              Заметка
            </label>
            <textarea
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="input"
              rows={2}
              placeholder="Комментарий..."
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setShowMealModal(false)}
              className="btn-secondary flex-1"
            >
              Отмена
            </button>
            <button type="submit" disabled={saving} className="btn-primary flex-1">
              {saving ? <LoadingSpinner size="sm" className="text-white" /> : 'Сохранить'}
            </button>
          </div>

          {editingMeal && (
            <button
              type="button"
              onClick={() => handleDeleteMeal(editingMeal)}
              className="btn-danger w-full"
            >
              <TrashIcon className="w-4 h-4 mr-2" />
              Удалить
            </button>
          )}
        </form>
      </Modal>
    </div>
  );
}
